import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@heroui/react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTranslation } from '@/hooks/useTranslation';
import { useInfiniteRecommendedGuides } from '@/hooks/useGuides';
import GuideCard from '@/components/guides/GuideCard';
import GuideCardSkeleton from '@/components/guides/GuideCardSkeleton';

const RecommendedGuidesSection: React.FC = () => {
  const { t } = useTranslation('home');
  const { currentLanguage } = useLanguage();
  const language = currentLanguage || 'en';

  const {
    data: recommendedData,
    fetchNextPage: fetchNextRecommendedPage,
    hasNextPage: hasNextRecommendedPage,
    isFetchingNextPage: isFetchingNextRecommendedPage,
    isLoading: recommendedLoading,
  } = useInfiniteRecommendedGuides(language);

  // Flatten recommended guides (limit to 6 initially)
  const recommendedGuides = useMemo(() => {
    const allGuides =
      recommendedData?.pages.flatMap((page) => {
        const response = page.data;
        return response || [];
      }) || [];
    return allGuides.slice(0, 6);
  }, [recommendedData]);

  if (recommendedGuides.length === 0) {
    return null;
  }

  return (
    <motion.section
      className="mb-12"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
    >
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{t('sections.recommended.title')}</h2>
        <p className="text-lg text-gray-600 dark:text-gray-400">{t('sections.recommended.subtitle')}</p>
      </div>

      {recommendedLoading && recommendedGuides.length === 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <GuideCardSkeleton key={i} />
          ))}
        </div>
      ) : recommendedGuides.length === 0 ? (
        <div className="text-center py-12 bg-white/60 dark:bg-gray-900/60 backdrop-blur-xl rounded-3xl border border-gray-200/50 dark:border-gray-800/50">
          <p className="text-gray-600 dark:text-gray-400">{t('noRecommended')}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            {recommendedGuides.map((guide) => (
              <GuideCard key={guide.id} guide={guide} language={language} />
            ))}
          </div>
          {/* TODO: Add load more button */}
          {/* {hasNextRecommendedPage && (
            <div className="flex justify-center">
              <Button
                color="primary"
                variant="flat"
                onPress={() => fetchNextRecommendedPage()}
                isLoading={isFetchingNextRecommendedPage}
                className="min-w-32"
              >
                {t('loadMore')}
              </Button>
            </div>
          )} */}
        </>
      )}
    </motion.section>
  );
};

export default RecommendedGuidesSection;
